import { Container, Sprite } from 'pixi.js';
import { SPRITES } from './sprites';

export interface EBullet {
  x: number; y: number;
  vx: number; vy: number;
  dmg: number;
  r: number;
  color: number;
  alive: boolean;
  ttl: number;
  homing: boolean;
  view: Sprite;
}

const POOL_SIZE = 300;

export class EBulletPool {
  pool: EBullet[] = [];
  private layer: Container;

  constructor(stage: Container) {
    this.layer = new Container();
    this.layer.blendMode = 'add';
    stage.addChild(this.layer);
  }

  spawn(opts: {
    x: number; y: number; vx: number; vy: number; dmg: number;
    r?: number; color?: number; ttl?: number; homing?: boolean;
  }): void {
    let b: EBullet | null = null;
    for (const cand of this.pool) {
      if (!cand.alive) { b = cand; break; }
    }
    if (!b) {
      if (this.pool.length >= POOL_SIZE) return;
      const view = new Sprite(SPRITES.particleGlow);
      view.anchor.set(0.5, 0.5);
      this.layer.addChild(view);
      b = {
        x: 0, y: 0, vx: 0, vy: 0, dmg: 0, r: 4,
        color: 0xff4444, alive: false, ttl: 0, homing: false, view,
      };
      this.pool.push(b);
    }
    b.x = opts.x;
    b.y = opts.y;
    b.vx = opts.vx;
    b.vy = opts.vy;
    b.dmg = opts.dmg;
    b.r = opts.r ?? 4;
    b.color = opts.color ?? 0xff4444;
    b.ttl = opts.ttl ?? 4;
    b.homing = opts.homing ?? false;
    b.alive = true;
    const s = (b.r * 2.4) / 32;
    b.view.scale.set(s, s);
    b.view.tint = b.color;
    b.view.alpha = 1;
    b.view.visible = true;
  }

  update(dt: number, W: number, H: number, target: { x: number; y: number }): void {
    for (const b of this.pool) {
      if (!b.alive) continue;
      if (b.homing) {
        const dx = target.x - b.x, dy = target.y - b.y;
        const d = Math.hypot(dx, dy) || 1;
        const sp = Math.hypot(b.vx, b.vy);
        const tx = (dx / d) * sp, ty = (dy / d) * sp;
        b.vx += (tx - b.vx) * Math.min(1, 200 / sp * dt);
        b.vy += (ty - b.vy) * Math.min(1, 200 / sp * dt);
      }
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.ttl -= dt;
      if (b.ttl <= 0 || b.x < -40 || b.x > W + 40 || b.y < -40 || b.y > H + 40) {
        b.alive = false;
        b.view.visible = false;
        continue;
      }
      b.view.x = b.x; b.view.y = b.y;
    }
  }

  kill(b: EBullet): void {
    b.alive = false;
    b.view.visible = false;
  }
}

export function enemyFire(
  e: any,
  pool: EBulletPool,
  target: { x: number; y: number },
  mirrorX?: number,
): void {
  if (e.fr <= 0) return;
  e.ft += 1;
  if (e.ft < e.fr) return;
  e.ft = 0;
  const ang = Math.atan2(target.y - e.y, target.x - e.x);
  const sp = 360;
  if (mirrorX !== undefined) {
    const real = pool;
    pool = {
      spawn(opts: Parameters<EBulletPool['spawn']>[0]) {
        real.spawn(opts);
        real.spawn({ ...opts, x: mirrorX, vx: -opts.vx });
      },
    } as EBulletPool;
  }
  if (e.kind === 'fighter') {
    pool.spawn({ x: e.x, y: e.y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, dmg: 8, color: 0xff6600 });
  } else if (e.kind === 'gunship') {
    for (const off of [-0.16, 0.16]) {
      const a = ang + off;
      pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, dmg: 7, color: 0xaa44ff });
    }
    if (Math.random() < 0.2) {
      pool.spawn({ x: e.x, y: e.y, vx: Math.cos(ang) * sp * 0.7, vy: Math.sin(ang) * sp * 0.7, dmg: 9, color: 0xff44ff, homing: true });
    }
  } else if (e.kind === 'bomber') {
    pool.spawn({ x: e.x, y: e.y, vx: Math.cos(ang) * sp * 0.6, vy: Math.sin(ang) * sp * 0.6, dmg: 18, r: 8, color: 0xff5500, ttl: 6 });
  } else if (e.kind === 'tank') {
    for (const off of [-0.22, 0, 0.22]) {
      const a = ang + off;
      pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, dmg: 11, r: 5, color: 0xcc7700 });
    }
  } else if (e.kind === 'interceptor') {
    pool.spawn({ x: e.x, y: e.y, vx: Math.cos(ang) * sp * 1.1, vy: Math.sin(ang) * sp * 1.1, dmg: 6, color: 0xffdd00 });
  } else if (e.kind === 'miniboss') {
    for (let i = 0; i < 3; i++) {
      const a = ang + (i - 1) * 0.22;
      pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp * 0.9, vy: Math.sin(a) * sp * 0.9, dmg: 8, color: 0xff4400 });
    }
  } else if (e.kind === 'boss') {
    const phase = (e as any).phase || 0;
    if (phase === 2) {
      // Phase 3: spiral + aimed
      const off = ((e as any).spinOff || 0) + 0.7;
      (e as any).spinOff = off;
      for (let i = 0; i < 4; i++) {
        const a = off + (i / 4) * Math.PI * 2;
        pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, dmg: 7, color: 0xff44ff });
      }
      for (let i = 0; i < 3; i++) {
        const a = ang + (i - 1) * 0.18;
        pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp * 1.1, vy: Math.sin(a) * sp * 1.1, dmg: 8, color: 0xff00ff });
      }
    } else if (phase === 1) {
      // Phase 2: spiral + aimed shot
      const off = ((e as any).spinOff || 0) + 0.4;
      (e as any).spinOff = off;
      for (let i = 0; i < 10; i++) {
        const a = off + (i / 10) * Math.PI * 2;
        pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp * 0.85, vy: Math.sin(a) * sp * 0.85, dmg: 7, color: 0xff44ff });
      }
      pool.spawn({ x: e.x, y: e.y, vx: Math.cos(ang) * sp * 1.4, vy: Math.sin(ang) * sp * 1.4, dmg: 10, color: 0xff00ff });
    } else {
      // Phase 1: 5-spread aimed
      for (let i = 0; i < 5; i++) {
        const a = ang + (i - 2) * 0.18;
        pool.spawn({ x: e.x, y: e.y, vx: Math.cos(a) * sp * 0.95, vy: Math.sin(a) * sp * 0.95, dmg: 9, color: 0xff00ff });
      }
    }
  }
}
